import React, { useEffect } from "react";
import Sidebar from "../Reusables/Sidebar";
import Navbar from "../Reusables/Navbar";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useQuery } from "@tanstack/react-query";
import empty from "../../assets/empty.png";
import {
  ekedcMonthlyReportKeys,
  fetchEkedcMonthlyReport,
} from "../api/ekedcReport";

const formatAmount = (value) => {
  if (value == null || value === "") return "₦0";
  const num = Number(value);
  if (Number.isNaN(num)) return "₦0";
  return `₦${num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
};

const formatMonth = (monthValue) => {
  if (!monthValue) return "—";
  const date = new Date(monthValue);
  if (Number.isNaN(date.getTime())) return monthValue;
  return date.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
};

const MonthlyEkedcReport = ({ title }) => {
  const navigate = useNavigate();
  const token = sessionStorage.getItem("token");

  useEffect(() => {
    if (!token) {
      navigate("/");
      toast.error("You aren't logged in");
    }
  }, [token, navigate]);

  const reportQuery = useQuery({
    queryKey: ekedcMonthlyReportKeys.all,
    queryFn: fetchEkedcMonthlyReport,
    enabled: !!token,
  });

  const rows = reportQuery.data?.data || [];

  return (
    <div className="flex flex-row h-screen overflow-hidden">
      <div className="w-[15%] shrink-0 h-screen">
        <Sidebar />
      </div>
      <div className="flex flex-col w-[85%] h-screen overflow-y-auto">
        <div className="w-[100%] shrink-0">
          <Navbar title={title} />
        </div>
        <div className="w-[100%] pt-2 pb-9 px-5 flex flex-col gap-6">
          <div className="flex flex-row justify-between items-center gap-3 flex-wrap">
            <span className="text-route-name text-[24px] font-semibold">
              Monthly EKEDC Report
            </span>
          </div>

          <div className="flex flex-col border border-input-color rounded-custom py-4 gap-4 bg-white">
            {reportQuery.isPending || reportQuery.isFetching ? (
              <div className="flex justify-center items-center py-16">
                <div
                  className="w-10 h-10 rounded-full animate-spin"
                  style={{
                    background:
                      "radial-gradient(farthest-side, #999999 94%, #0000) top/6px 6px no-repeat, conic-gradient(#0000 30%, #999999)",
                    WebkitMask:
                      "radial-gradient(farthest-side, #0000 calc(100% - 6px), #000 0)",
                    mask: "radial-gradient(farthest-side, #0000 calc(100% - 6px), #000 0)",
                  }}
                  aria-label="Loading monthly report"
                />
              </div>
            ) : reportQuery.isError ? (
              <div className="flex flex-col justify-center items-center py-10">
                <p className="text-[13px] text-red-500">
                  {reportQuery.error?.message ||
                    "Failed to load monthly EKEDC report"}
                </p>
              </div>
            ) : rows.length > 0 ? (
              <div className="overflow-x-auto px-3">
                <table className="w-full text-left text-[12px] min-w-[900px]">
                  <thead>
                    <tr className="border-b text-route-noncolor">
                      <th className="py-3 px-2 font-semibold">S/N</th>
                      <th className="py-3 px-2 font-semibold">Month</th>
                      <th className="py-3 px-2 font-semibold">
                        Opening Balance
                      </th>
                      <th className="py-3 px-2 font-semibold">
                        Amount Payable to EKEDC
                      </th>
                      <th className="py-3 px-2 font-semibold">
                        Amount Paid to EKEDC
                      </th>
                      <th className="py-3 px-2 font-semibold">
                        Closing Balance
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <tr
                        key={row.id ?? `${row.month}-${index}`}
                        className="border-b last:border-b-0 text-black"
                      >
                        <td className="py-3 px-2">{index + 1}</td>
                        <td className="py-3 px-2 font-medium">
                          {formatMonth(row.month)}
                        </td>
                        <td className="py-3 px-2">
                          {formatAmount(row.openingBalance)}
                        </td>
                        <td className="py-3 px-2">
                          {formatAmount(row.amountPayable)}
                        </td>
                        <td className="py-3 px-2">
                          {formatAmount(row.amountPaid)}
                        </td>
                        <td className="py-3 px-2 font-medium">
                          {formatAmount(row.closingBalance)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex flex-col justify-center items-center py-10">
                <img src={empty} alt="No monthly report data" />
                <p className="text-[13px] text-route-noncolor mt-2">
                  No monthly EKEDC report data found
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonthlyEkedcReport;
